import { tBoards } from '../../../lib/boards-i18n';

/**
 * 목록 위 칸 넷의 «계산» — 그리는 것과 재는 것을 갈라 둔다 (2026-09-17).
 *
 * 좌표·비율은 시험이 숫자로 잴 수 있어야 한다. 컴포넌트 안에 두면 DOM 을 통해서만 잴 수 있고,
 * 그러면 「선이 어디를 지나는가」 대신 「path 글자가 있는가」를 재게 된다(그건 아무것도 안 잰다).
 */

/** 시안 `.viz-line` 의 그림판 — viewBox 270×88, 왼쪽 18 은 눈금 글자 자리. */
const W = 270;
const H = 88;
const PAD_X = 18;
const PAD_TOP = 8;
const PAD_BOTTOM = 10;

export interface LinePlot {
  w: number; h: number; padX: number;
  /** 가운데 눈금선의 y 와 그 값. */
  midY: number; midLabel: string;
  points: Array<{ x: number; y: number }>;
  lineD: string;
  areaD: string;
  last: { x: number; y: number } | null;
}

/**
 * 값 계열을 선 차트 좌표로. **값이 0개·1개면 선을 그리지 않는다** — 점 하나를 이어 선이라 하면
 * 「추세가 있다」는 거짓말이 된다(그때는 끝점만 찍는다).
 *
 * ★ 최댓값이 0 이어도 나누지 않는다(전부 0 이면 바닥에 눕는다).
 */
export function buildLinePlot(values: readonly number[]): LinePlot {
  const max = Math.max(...values, 0);
  const span = max > 0 ? max : 1;
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const y = (v: number) => PAD_TOP + plotH * (1 - v / span);
  const step = values.length > 1 ? (W - PAD_X) / (values.length - 1) : 0;
  const points = values.map((v, i) => ({
    x: Math.round((PAD_X + step * i) * 10) / 10,
    y: Math.round(y(v) * 10) / 10,
  }));

  const lineD = points.length > 1
    ? points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ')
    : '';
  const areaD = points.length > 1
    ? `${lineD} L${points[points.length - 1]!.x} ${H - PAD_BOTTOM} L${points[0]!.x} ${H - PAD_BOTTOM} Z`
    : '';

  // 가운데 눈금 — 최댓값의 절반. 전부 0 이면 0 을 적는다(있지도 않은 눈금을 지어내지 않는다).
  const midValue = max > 0 ? Math.round(max / 2) : 0;
  return {
    w: W, h: H, padX: PAD_X,
    midY: Math.round(y(midValue) * 10) / 10,
    midLabel: String(midValue),
    points, lineD, areaD,
    last: points.length > 0 ? points[points.length - 1]! : null,
  };
}

export interface ConfirmRing {
  title: string;
  total: number;
  confirmed: number;
  percent: number;
  rows: Array<{ key: 'confirmed' | 'readOnly' | 'unread'; label: string; count: number; ratio: number }>;
}

/**
 * 확인 요청 공지 한 건의 확인 현황을 도넛 값으로.
 *
 * ★★★ **분모는 `targetCount`** 다 — 「읽을 수 있는 사람」. 서버가 그것을 안 주면(목록 응답 등)
 *    도넛을 그리지 않는다(`null`). `readCount` 를 분모로 삼으면 **읽은 사람만 센 100%** 가 되어
 *    「다 확인했다」는 거짓이 된다. 그 실수가 이 함수를 따로 둔 이유다.
 * ★ `readOnly` = 열었지만 확인 안 누른 사람 = `readCount - confirmCount`. 음수가 나오면 0 으로 눕힌다
 *   (확인이 읽음보다 많을 수는 없지만, 두 수가 다른 시점에 세어졌을 수 있다).
 */
export function confirmRing(src: {
  title: string;
  targetCount?: number;
  readCount?: number;
  confirmCount?: number;
}): ConfirmRing | null {
  const total = src.targetCount;
  if (typeof total !== 'number' || total <= 0) return null;

  const confirmed = Math.max(0, Math.min(total, src.confirmCount ?? 0));
  const read = Math.max(0, Math.min(total, src.readCount ?? 0));
  const readOnly = Math.max(0, read - confirmed);
  const unread = Math.max(0, total - confirmed - readOnly);
  const pctOf = (n: number) => Math.round((n / total) * 1000) / 10;

  return {
    title: src.title,
    total,
    confirmed,
    percent: Math.round((confirmed / total) * 100),
    rows: [
      { key: 'confirmed', label: tBoards('boards.v1.kpi.state.confirmed'), count: confirmed, ratio: pctOf(confirmed) },
      { key: 'readOnly', label: tBoards('boards.v1.kpi.state.read_only'), count: readOnly, ratio: pctOf(readOnly) },
      { key: 'unread', label: tBoards('boards.v1.kpi.state.unread'), count: unread, ratio: pctOf(unread) },
    ],
  };
}
